import { apiClient } from 'shared/api/client';
import { endpoints } from 'shared/api/endpoints';
import { mockCourses } from 'shared/api/fixtures';
import { fetchWithFallback } from 'shared/data/fetchWithFallback';
import { mapDbCourse } from 'shared/data/mappers';
import { getSupabase } from 'shared/supabase/client';
import type { Course } from 'types';

async function fetchCoursesFromApi(schoolSlug?: string): Promise<Course[]> {
  const { data } = await apiClient.get<Course[]>(endpoints.courses.list, {
    params: schoolSlug ? { school: schoolSlug } : undefined,
  });
  return data;
}

async function fetchCoursesFromSupabase(schoolSlug?: string): Promise<Course[]> {
  let query = getSupabase()
    .from('inst_courses')
    .select('*, core_schools!inner(slug), instructor:core_users!instructor_id(first_name, last_name)')
    .is('deleted_at', null);

  if (schoolSlug) {
    query = query.eq('core_schools.slug', schoolSlug);
  }

  const { data, error } = await query;
  if (error) throw error;
  return (data ?? []).map((row) => mapDbCourse(row));
}

export async function fetchCourses(schoolSlug?: string): Promise<Course[]> {
  try {
    return await fetchWithFallback(
      () => fetchCoursesFromSupabase(schoolSlug),
      () => fetchCoursesFromApi(schoolSlug),
    );
  } catch (err) {
    console.warn('Courses unavailable, using fixture data:', err);
    const courses = mockCourses;
    return schoolSlug ? courses.filter((c) => c.schoolSlug === schoolSlug) : courses;
  }
}

export async function fetchCourseById(id: string): Promise<Course> {
  return fetchWithFallback(
    async () => {
      const { data, error } = await getSupabase()
        .from('inst_courses')
        .select('*, core_schools!inner(slug), instructor:core_users!instructor_id(first_name, last_name)')
        .eq('id', id)
        .is('deleted_at', null)
        .single();
      if (error) throw error;
      return mapDbCourse(data);
    },
    async () => {
      const { data } = await apiClient.get<Course>(endpoints.courses.detail(id));
      return data;
    }
  );
}
